import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { resolveInitialLocale, langChoiceKey } from '@/lib/locales';
import { fireLeadTracking } from '@/lib/leadTrackingDispatcher';
import { deriveLeadSources, slugifyIdentifier } from '@/lib/leadSources';
import { Loader2, Send, Check, MapPin, Phone, Mail, ExternalLink, Download, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useGTM } from '@/hooks/useGTM';
import BrandFooter from '@/components/brand/BrandFooter';
import TrackingScripts from '@/components/tracking/TrackingScripts';
import { LEGAL_LINKS } from '@/lib/legalLinks';
import LanguageSwitcher from '@/components/i18n/LanguageSwitcher';
import { OptimizedImage } from '@/components/OptimizedImage';
import { heroAlignment } from '@/lib/heroAlignment';
import { playerEmbedUrl } from '@/lib/videoEmbed';
import { HeroVideoBackground, canPlayAsBackground } from '@/components/HeroVideoBackground';
import { sendViewBeacon, utmFromLocation } from '@/lib/viewBeacon';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Section Renderers
function HeroSection({ content, theme }) {
  const align = heroAlignment(content.alignment);
  const bgType = content.background_type || 'image';
  
  const getBackground = () => {
    if (bgType === 'gradient') {
      return `linear-gradient(135deg, ${content.gradient_start || '#144733'} 0%, ${content.gradient_end || '#1e6e5c'} 100%)`;
    }
    if (bgType === 'image' && content.background_image) {
      return `url(${content.background_image})`;
    }
    return 'linear-gradient(135deg, #144733 0%, #1e6e5c 100%)';
  };
  
  return (
    <div
      className={`relative flex items-center ${align.row} text-white overflow-hidden`}
      style={{
        height: content.height || '60vh',
        minHeight: '300px',
      }}
    >
      {/* Video Background */}
      {/* Image/gradient always sits underneath, so an embed that is still
          loading (or a link that can't play) never leaves the hero blank */}
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: getBackground(),
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      {bgType === 'video' && canPlayAsBackground(content.background_video) && (
        <HeroVideoBackground
          url={content.background_video}
          muted={content.video_muted !== false}
          loop={content.video_loop !== false}
        />
      )}
      <div
        className="absolute inset-0 bg-black"
        style={{ opacity: content.overlay_opacity ?? 0.4 }}
      />
      <div className={`relative z-10 ${align.text} px-6 md:px-12 max-w-4xl`}>
        <h1 className="text-3xl md:text-5xl font-bold mb-4">{content.headline}</h1>
        {content.subheadline && (
          <p className="text-lg md:text-xl opacity-90">{content.subheadline}</p>
        )}
        {content.cta_text && content.cta_url && (
          <Button
            className="mt-6"
            size="lg"
            onClick={() => window.open(content.cta_url, '_blank')}
          >
            {content.cta_text}
          </Button>
        )}
      </div>
    </div>
  );
}

function LogoOverlaySection({ content, brand }) {
  // Get logo from content or fall back to brand logo
  const logoUrl = content.logo_url || brand?.logo_url;
  const logoFallback = content.logo_fallback_url || brand?.logo_fallback_url;
  
  if (!logoUrl) return null;
  
  const size = content.size || 'large';
  const sizeClasses = {
    small: 'h-16 md:h-20',
    medium: 'h-24 md:h-32',
    large: 'h-32 md:h-40',
  };
  
  const shapeClasses = {
    square: 'rounded-none',
    rounded: 'rounded-xl',
    circle: 'rounded-full',
  };
  
  const borderStyles = {
    none: 'none',
    thin: '1px solid',
    medium: '2px solid',
    thick: '4px solid',
  };
  
  // Get actual values with defaults matching the editor
  const backgroundStyle = content.background_style || 'none'; // Default to none (transparent)
  const borderStyle = content.border_style || 'none'; // Default to none
  
  // Check if we need a container (has background or border)
  const hasBackground = backgroundStyle !== 'none';
  const hasBorder = borderStyle !== 'none';
  const needsContainer = hasBackground || hasBorder;
  
  const containerStyle = {
    backgroundColor: hasBackground ? (content.background_color || 'white') : 'transparent',
    border: hasBorder 
      ? `${borderStyles[borderStyle]} ${content.border_color || '#e5e7eb'}` 
      : 'none',
    position: 'absolute',
    top: content.top_position || '0px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 30,
  };
  
  // Only add padding and shadow if we have a container background
  const paddingClass = needsContainer ? 'p-4 md:p-6' : '';
  const shadowClass = hasBackground ? 'shadow-2xl' : '';
  
  return (
    <div 
      className={`${paddingClass} ${shapeClasses[content.shape || 'square']} ${shadowClass}`}
      style={containerStyle}
    >
      <OptimizedImage
        src={logoUrl}
        fallbackSrc={logoFallback}
        alt={brand?.footer?.company_name || 'Logo'}
        className={`${sizeClasses[size]} w-auto object-contain`}
      />
    </div>
  );
}

function TextSection({ content }) {
  return (
    <div
      className="py-12 px-6 max-w-4xl mx-auto"
      style={{ textAlign: content.alignment || 'left' }}
    >
      {content.title && (
        <h2 className="text-2xl md:text-3xl font-bold mb-4">{content.title}</h2>
      )}
      {content.body && (
        <div className="prose prose-lg max-w-none">
          {content.body.split('\n').map((p, i) => (
            <p key={i} className="text-muted-foreground mb-4">{p}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function PropertyDetailsSection({ content }) {
  return (
    <div className="py-10 px-6">
      <div className="max-w-4xl mx-auto">
        {content.price && (
          <div className="text-center mb-6">
            <p className="text-sm text-muted-foreground uppercase tracking-wider">
              {content.price_label || 'Price'}
            </p>
            <p className="text-3xl md:text-4xl font-bold text-primary">{content.price}</p>
          </div>
        )}
        <div className="flex flex-wrap justify-center gap-8 text-center">
          {content.bedrooms && (
            <div>
              <p className="text-2xl font-bold">{content.bedrooms}</p>
              <p className="text-sm text-muted-foreground">Bedrooms</p>
            </div>
          )}
          {content.bathrooms && (
            <div>
              <p className="text-2xl font-bold">{content.bathrooms}</p>
              <p className="text-sm text-muted-foreground">Bathrooms</p>
            </div>
          )}
          {content.garages && (
            <div>
              <p className="text-2xl font-bold">{content.garages}</p>
              <p className="text-sm text-muted-foreground">Garage</p>
            </div>
          )}
          {content.property_size && (
            <div>
              <p className="text-2xl font-bold">{content.property_size}</p>
              <p className="text-sm text-muted-foreground">Floor Area</p>
            </div>
          )}
          {content.land_size && (
            <div>
              <p className="text-2xl font-bold">{content.land_size}</p>
              <p className="text-sm text-muted-foreground">Land Size</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ImageGallerySection({ content }) {
  const images = content.images || [];
  if (images.length === 0) return null;
  
  return (
    <div className="py-10 px-6">
      <div className="max-w-6xl mx-auto">
        {content.title && (
          <h2 className="text-2xl font-bold mb-6 text-center">{content.title}</h2>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {images.map((img, idx) => (
            <div key={idx} className="aspect-video rounded-lg overflow-hidden bg-muted">
              <OptimizedImage
                src={img.url || img}
                fallbackSrc={img.fallback_url}
                alt={img.alt || `Image ${idx + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FeaturesSection({ content }) {
  const features = content.features || [];
  return (
    <div className="py-12 px-6">
      <div className="max-w-4xl mx-auto">
        {content.title && (
          <h2 className="text-2xl font-bold mb-8 text-center">{content.title}</h2>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {features.map((feature, idx) => {
            // Handle different feature data structures
            const featureText = typeof feature === 'string' 
              ? feature 
              : feature.title || feature.text || '';
            const featureDescription = typeof feature === 'object' 
              ? feature.description 
              : null;
            
            return (
              <div key={idx} className="flex items-start gap-3 p-4">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="font-medium">{featureText}</p>
                  {featureDescription && (
                    <p className="text-sm text-muted-foreground">{featureDescription}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function AgentCardSection({ content, sectionId, onLead }) {
  return (
    <div className="py-12 px-6">
      <div className="max-w-md mx-auto text-center">
        {content.agent_photo && (
          <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden bg-muted">
            <OptimizedImage
              src={content.agent_photo}
              fallbackSrc={content.agent_photo_fallback}
              alt={content.agent_name}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <h3 className="text-xl font-bold">{content.agent_name}</h3>
        {content.agent_title && (
          <p className="text-muted-foreground">{content.agent_title}</p>
        )}
        {content.agent_bio && (
          <p className="mt-4 text-sm text-muted-foreground">{content.agent_bio}</p>
        )}
        <div className="mt-6 flex justify-center gap-4">
          {content.agent_phone && (
            <a
              href={`tel:${content.agent_phone}`}
              onClick={() => onLead?.(`${sectionId}:phone`)}
              className="flex items-center gap-2 text-sm text-primary hover:underline"
            >
              <Phone className="w-4 h-4" />
              {content.agent_phone}
            </a>
          )}
          {content.agent_email && (
            <a
              href={`mailto:${content.agent_email}`}
              onClick={() => onLead?.(`${sectionId}:email`)}
              className="flex items-center gap-2 text-sm text-primary hover:underline"
            >
              <Mail className="w-4 h-4" />
              Email
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export function DynamicAgentSection({ content, sectionId, onLead }) {
  // The agent arrives already resolved in the page lookup (content.agent —
  // see _embed_dynamic_agents in landing_pages/router.py). This used to fetch
  // /reference-data/agents/list?per_page=200 from the browser, which is an
  // authenticated endpoint: every anonymous visitor got a 401 and the section
  // rendered blank. It is null when the agent is missing or no longer active.
  const agent = content.agent || null;

  // The lead_identifier is that agent's own slug rather than the section
  // heading — this is what distinguishes Agent A from Agent B in GA4 when a
  // page carries several dynamic agents.
  const leadFor = (role) => () =>
    onLead?.(`${sectionId}:${role}`, { identifier: slugifyIdentifier(agent?.name, 'agent') });

  if (!agent) {
    return null;
  }
  
  const layout = content.layout || 'card';
  const showBadges = content.show_badges !== false;
  const showGallery = content.show_gallery !== false && layout !== 'minimal';
  
  // Get available images
  const availableImages = agent.images 
    ? Object.entries(agent.images).filter(([key, url]) => url && key !== 'high_res')
    : [];
  
  // Minimal layout
  if (layout === 'minimal') {
    return (
      <div className="py-8 px-6">
        <div className="max-w-2xl mx-auto text-center">
          {content.title && (
            <h2 className="text-xl font-bold mb-4">{content.title}</h2>
          )}
          <p className="font-semibold text-lg">{agent.name}</p>
          {agent.title && <p className="text-muted-foreground">{agent.title}</p>}
          <div className="mt-4 flex justify-center gap-6 flex-wrap">
            {agent.phone && (
              <a href={`tel:${agent.phone}`} onClick={leadFor('phone')} className="flex items-center gap-2 text-primary hover:underline">
                <Phone className="w-4 h-4" />
                {agent.phone}
              </a>
            )}
            {agent.email && (
              <a href={`mailto:${agent.email}`} onClick={leadFor('email')} className="flex items-center gap-2 text-primary hover:underline">
                <Mail className="w-4 h-4" />
                Email
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // Card layout (compact)
  if (layout === 'card') {
    const displayImage = content.display_image || 'high_res';
    const imageUrl = agent.images?.[displayImage] || agent.images?.high_res;
    
    return (
      <div className="py-12 px-6">
        <div className="max-w-md mx-auto text-center">
          {content.title && (
            <h2 className="text-2xl font-bold mb-6">{content.title}</h2>
          )}
          {imageUrl && (
            <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden bg-muted">
              <OptimizedImage
                src={imageUrl}
                alt={agent.name}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <h3 className="text-xl font-bold">{agent.name}</h3>
          {agent.title && <p className="text-muted-foreground">{agent.title}</p>}
          
          {showBadges && (agent.is_emerald_circle || agent.is_gold_club || agent.has_ffc) && (
            <div className="mt-3 flex justify-center gap-2 flex-wrap">
              {agent.is_emerald_circle && (
                <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                  Emerald Circle
                </span>
              )}
              {agent.is_gold_club && (
                <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300">
                  Gold Club
                </span>
              )}
              {agent.has_ffc && (
                <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                  FFC: {agent.ffc_type}
                </span>
              )}
            </div>
          )}
          
          <div className="mt-6 flex justify-center gap-4">
            {agent.phone && (
              <a href={`tel:${agent.phone}`} onClick={leadFor('phone')} className="flex items-center gap-2 text-sm text-primary hover:underline">
                <Phone className="w-4 h-4" />
                {agent.phone}
              </a>
            )}
            {agent.email && (
              <a href={`mailto:${agent.email}`} onClick={leadFor('email')} className="flex items-center gap-2 text-sm text-primary hover:underline">
                <Mail className="w-4 h-4" />
                Email
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // Full layout with gallery
  const displayImage = content.display_image || 'high_res';
  const imageUrl = agent.images?.[displayImage] || agent.images?.high_res;
  
  return (
    <div className="py-12 px-6">
      <div className="max-w-4xl mx-auto">
        {content.title && (
          <h2 className="text-2xl font-bold mb-8 text-center">{content.title}</h2>
        )}
        
        <div className="flex flex-col md:flex-row gap-8 items-start">
          {/* Agent Photo */}
          <div className="flex-shrink-0 mx-auto md:mx-0">
            {imageUrl ? (
              <div className="w-40 h-40 rounded-full overflow-hidden bg-muted">
                <OptimizedImage
                  src={imageUrl}
                  alt={agent.name}
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="w-40 h-40 rounded-full bg-muted flex items-center justify-center">
                <span className="text-4xl font-semibold text-muted-foreground">
                  {agent.first_name?.[0]}{agent.last_name?.[0]}
                </span>
              </div>
            )}
          </div>
          
          {/* Agent Info */}
          <div className="flex-1 text-center md:text-left">
            <h3 className="text-2xl font-bold">{agent.name}</h3>
            {agent.title && <p className="text-lg text-muted-foreground">{agent.title}</p>}
            
            {showBadges && (agent.is_emerald_circle || agent.is_gold_club || agent.has_ffc) && (
              <div className="mt-3 flex justify-center md:justify-start gap-2 flex-wrap">
                {agent.is_emerald_circle && (
                  <span className="px-3 py-1 text-sm rounded-full bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                    Emerald Circle
                  </span>
                )}
                {agent.is_gold_club && (
                  <span className="px-3 py-1 text-sm rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300">
                    Gold Club
                  </span>
                )}
                {agent.has_ffc && (
                  <span className="px-3 py-1 text-sm rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                    FFC: {agent.ffc_type}
                  </span>
                )}
              </div>
            )}
            
            {agent.description && (
              <p className="mt-4 text-muted-foreground">{agent.description}</p>
            )}
            
            <div className="mt-6 flex flex-wrap justify-center md:justify-start gap-4">
              {agent.phone && (
                <a href={`tel:${agent.phone}`} onClick={leadFor('phone')} className="flex items-center gap-2 text-primary hover:underline">
                  <Phone className="w-5 h-5" />
                  {agent.phone}
                </a>
              )}
              {agent.email && (
                <a href={`mailto:${agent.email}`} onClick={leadFor('email')} className="flex items-center gap-2 text-primary hover:underline">
                  <Mail className="w-5 h-5" />
                  {agent.email}
                </a>
              )}
            </div>
          </div>
        </div>
        
        {/* Image Gallery */}
        {showGallery && availableImages.length > 0 && (
          <div className="mt-10">
            <h4 className="text-lg font-semibold mb-4 text-center md:text-left">Gallery</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {availableImages.map(([key, url]) => (
                <div key={key} className="aspect-square overflow-hidden rounded-lg bg-muted">
                  <OptimizedImage
                    src={url}
                    alt={`${agent.name} - ${key.replace(/_/g, ' ')}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MapSection({ content }) {
  if (!content.address && !content.embed_url) return null;
  
  return (
    <div className="py-10 px-6">
      <div className="max-w-4xl mx-auto">
        {content.title && (
          <h2 className="text-2xl font-bold mb-6 text-center">{content.title}</h2>
        )}
        {content.address && (
          <div className="flex items-center justify-center gap-2 mb-4 text-muted-foreground">
            <MapPin className="w-4 h-4" />
            <span>{content.address}</span>
          </div>
        )}
        {content.embed_url && (
          <div className="aspect-video rounded-lg overflow-hidden">
            <iframe
              src={content.embed_url}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        )}
      </div>
    </div>
  );
}

function VideoSection({ content }) {
  if (!content.video_url) return null;
  
  const embedUrl = playerEmbedUrl(content.video_url);
  
  return (
    <div className="py-10 px-6">
      <div className="max-w-4xl mx-auto">
        {content.title && (
          <h2 className="text-2xl font-bold mb-6 text-center">{content.title}</h2>
        )}
        <div className="aspect-video rounded-lg overflow-hidden bg-muted">
          <iframe
            src={embedUrl}
            width="100%"
            height="100%"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );
}

// UTM tags on the page URL travel with the submission so leads can be
// split by campaign (helper shared with the view beacon).
export { utmFromLocation };

export function ContactFormSection({ content, form, slug, locale, onFormSubmit }) {
  const [formData, setFormData] = useState({});
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  const fields = form?.fields || [];
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/public/pages/${slug}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          _source: utmFromLocation(),
          // Visit context: the language shown, where the visitor came from
          // (the request's own Referer is only the pages host) and the path.
          ...(locale ? { _locale: locale } : {}),
          ...(document.referrer ? { _referrer: document.referrer } : {}),
          _path: window.location.pathname,
          ...(form?.marketing_consent_enabled ? { _marketing_consent: marketingConsent } : {}),
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSubmitted(true);
        setSuccessMessage(data.data?.message || 'Thank you for your submission!');
        // Track only after the backend confirms that the submission was saved.
        if (onFormSubmit) onFormSubmit({ submissionId: data.data?.id });
      } else {
        toast.error(data.detail || 'Failed to submit form');
      }
    } catch (error) {
      toast.error('Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (submitted) {
    return (
      <div className="py-12 px-6">
        <div className="max-w-md mx-auto text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
            <Check className="w-8 h-8 text-primary" />
          </div>
          <h3 className="text-xl font-bold mb-2">Thank You!</h3>
          <p className="text-muted-foreground">{successMessage}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="py-12 px-6" id="contact-form">
      <div className="max-w-lg mx-auto">
        {content.title && (
          <h2 className="text-2xl font-bold mb-2 text-center">{content.title}</h2>
        )}
        {content.subtitle && (
          <p className="text-muted-foreground text-center mb-6">{content.subtitle}</p>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {fields.sort((a, b) => a.order - b.order).map((field) => (
            <div key={field.id}>
              <label className="text-sm font-medium mb-1 block">
                {field.label}
                {field.required && <span className="text-destructive ml-1">*</span>}
              </label>
              
              {field.type === 'textarea' ? (
                <Textarea
                  placeholder={field.placeholder}
                  required={field.required}
                  value={formData[field.name] || ''}
                  onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                  rows={4}
                  className="bg-white dark:bg-white border-gray-300 text-gray-900 focus:border-primary"
                />
              ) : field.type === 'select' ? (
                <select
                  className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  required={field.required}
                  value={formData[field.name] || ''}
                  onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                  style={{ colorScheme: 'light' }}
                >
                  <option value="">{field.placeholder || `Select ${field.label}`}</option>
                  {field.options?.map((opt, i) => (
                    <option key={i} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              ) : field.type === 'checkbox' ? (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData[field.name] || false}
                    onChange={(e) => setFormData({ ...formData, [field.name]: e.target.checked })}
                    className="rounded border-gray-300 bg-white text-primary focus:ring-primary"
                  />
                  <span className="text-sm">{field.placeholder}</span>
                </div>
              ) : field.type === 'radio' ? (
                <div className="space-y-2">
                  {field.options?.map((opt, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        type="radio"
                        name={field.name}
                        value={opt.value}
                        checked={formData[field.name] === opt.value}
                        onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                        className="border-gray-300 bg-white text-primary focus:ring-primary"
                      />
                      <span className="text-sm">{opt.label}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <Input
                  type={field.type}
                  placeholder={field.placeholder}
                  required={field.required}
                  value={formData[field.name] || ''}
                  onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                  className="bg-white dark:bg-white border-gray-300 text-gray-900 focus:border-primary"
                />
              )}
            </div>
          ))}

          {form?.marketing_consent_enabled && (
            <label className="flex items-start gap-2 text-sm" data-testid="marketing-consent">
              <input
                type="checkbox"
                checked={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.checked)}
                className="mt-0.5 rounded border-gray-300 bg-white text-primary focus:ring-primary"
              />
              <span>{form.marketing_consent_text}</span>
            </label>
          )}

          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
            ) : (
              <><Send className="w-4 h-4 mr-2" /> {form?.submit_button_text || 'Submit'}</>
            )}
          </Button>

          {/* POPIA s18: tell the visitor, at collection, who uses their details and why. */}
          <p className="text-xs text-gray-500" data-testid="form-privacy-notice">
            Pam Golding Properties will use the details you submit to respond to your
            enquiry, as set out in our{' '}
            <a
              href={LEGAL_LINKS.privacy.href}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-gray-700"
            >
              Privacy Policy
            </a>
            .
          </p>
        </form>
      </div>
    </div>
  );
}

function PDFDownloadSection({ content }) {
  const alignmentClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
  };
  
  const alignment = content.alignment || 'center';
  const buttonStyle = content.button_style || 'primary';
  
  if (!content.pdf_url) return null;
  
  return (
    <div className={`py-12 px-6 ${alignmentClasses[alignment]}`}>
      <div className="max-w-4xl mx-auto">
        {content.title && (
          <h2 className="text-2xl font-bold mb-2">{content.title}</h2>
        )}
        {content.description && (
          <p className="text-muted-foreground mb-6">{content.description}</p>
        )}
        <a
          href={content.pdf_url}
          target="_blank"
          rel="noopener noreferrer"
          download={content.pdf_filename || 'document.pdf'}
          className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
            buttonStyle === 'primary' 
              ? 'bg-primary text-primary-foreground hover:opacity-90' 
              : buttonStyle === 'outline'
              ? 'border-2 border-primary text-primary hover:bg-primary/10'
              : 'text-primary hover:bg-primary/10'
          }`}
          style={buttonStyle === 'primary' ? { backgroundColor: 'var(--brand-button)', color: 'var(--brand-button-text)' } : {}}
        >
          <Download className="w-5 h-5" />
          {content.button_text || 'Download PDF'}
        </a>
      </div>
    </div>
  );
}

function DocumentListSection({ content }) {
  const documents = content.documents || [];
  
  if (documents.length === 0) return null;
  
  return (
    <div className="py-12 px-6">
      <div className="max-w-6xl mx-auto">
        {content.title && (
          <h2 className="text-2xl font-bold mb-2 text-center">{content.title}</h2>
        )}
        {content.description && (
          <p className="text-muted-foreground mb-8 text-center max-w-2xl mx-auto">{content.description}</p>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {documents.map((doc, index) => (
            <a
              key={doc.id || index}
              href={doc.pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              download={doc.pdf_filename || 'document.pdf'}
              className="group block bg-card border rounded-xl overflow-hidden transition-all hover:shadow-lg hover:border-primary/50"
            >
              {doc.image_url ? (
                <div className="aspect-[16/10] overflow-hidden">
                  <OptimizedImage
                    src={doc.image_url}
                    fallbackSrc={doc.image_fallback_url}
                    alt={doc.title || 'Document'}
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                  />
                </div>
              ) : (
                <div className="aspect-[16/10] bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
                  <FileText className="w-16 h-16 text-red-400" />
                </div>
              )}
              <div className="p-4">
                <h3 className="font-semibold text-lg mb-1 group-hover:text-primary transition-colors">
                  {doc.title || 'Document'}
                </h3>
                {doc.description && (
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{doc.description}</p>
                )}
                <div className="flex items-center text-sm font-medium text-primary">
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

// Main Public Landing Page Component
export default function PublicLandingPage({ preview = false }) {
  const { slug, locale } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [page, setPage] = useState(null);
  const [brand, setBrand] = useState(null);
  const [locales, setLocales] = useState([]);       // languages this page offers
  const [activeLocale, setActiveLocale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { trackPageView, trackFormSubmit, trackLead, gtmEnabled } = useGTM({ preview });

  // Lead-conversion tracking: discover the page's lead sources once, then fire
  // the configured platform events (consent-gated, defensive) on interaction.
  const leadSourceById = useMemo(() => {
    const map = {};
    for (const s of deriveLeadSources(page, brand)) map[s.id] = s;
    return map;
  }, [page, brand]);
  const fireLead = useCallback((sourceId, opts = {}) => {
    if (preview) return;   // never fire real conversions from the editor preview
    const src = leadSourceById[sourceId];
    if (!src) return;

    // 1. ALWAYS: the standard GA4 lead contract, pushed to the global GTM
    //    dataLayer. Independent of any paid-media configuration.
    //    `identifier` may be overridden by the caller where the contact only
    //    resolves at runtime (dynamic agent sections).
    trackLead(src.type, opts.identifier || src.identifier);

    // 2. IF CONFIGURED: the optional, consent-gated paid-media conversions.
    fireLeadTracking({ page, sourceId, transactionId: opts.transactionId });
  }, [page, leadSourceById, preview, trackLead]);

  // Generate CSS custom properties from brand colors
  const brandStyles = brand?.colors ? {
    '--brand-primary': brand.colors.primary || '#144733',
    '--brand-secondary': brand.colors.secondary || '#1e6e5c',
    '--brand-button': brand.colors.button || brand.colors.primary || '#144733',
    '--brand-button-text': brand.colors.button_text || '#ffffff',
    '--brand-background': brand.colors.background || '#ffffff',
    '--brand-section-bg': brand.colors.section_background || '#ffffff',
    '--brand-section-bg-alt': brand.colors.section_background_alt || '#f8fafc',
    '--brand-text': brand.colors.text || '#1f2937',
    '--brand-text-muted': brand.colors.text_muted || '#6b7280',
  } : {};
  
  useEffect(() => {
    fetchPage();
    fetchBrand();
  }, [slug, locale]);

  // Reflect the served language to the browser + assistive tech.
  useEffect(() => {
    if (activeLocale) document.documentElement.lang = activeLocale;
  }, [activeLocale]);

  // Browser tab / <title> = the page's own name (preferring an explicit,
  // localised SEO title, else the page name). Without this every public page
  // shows the app's static default title. Skipped in the editor preview so it
  // doesn't rename the builder tab; restores the previous title on unmount.
  useEffect(() => {
    if (preview || !page) return;
    const name = (page.meta_title && page.meta_title.trim()) || page.title;
    if (!name) return;
    const previous = document.title;
    document.title = name;
    return () => { document.title = previous; };
  }, [page, preview]);

  // Auto-detect: on the bare /p/{slug} URL (no explicit locale, not preview),
  // send a visitor to their browser's language if the page offers it — unless
  // they've already picked one via the switcher. Known once locales load.
  const autoLocaleTarget = useMemo(() => {
    if (preview || locale || !locales.length) return null;
    let stored = null;
    try { stored = sessionStorage.getItem(langChoiceKey(slug)); } catch { /* private mode */ }
    const browserLangs = (typeof navigator !== 'undefined'
      && (navigator.languages || (navigator.language ? [navigator.language] : []))) || [];
    return resolveInitialLocale({
      locales,
      defaultLocale: page?.default_locale || 'en',
      stored,
      browserLangs,
    }) || null;
  }, [locales, locale, preview, slug, page?.default_locale]);

  useEffect(() => {
    // Keep the query string: it carries the UTM tags the view and the
    // form submission are attributed to.
    if (autoLocaleTarget) navigate(`/p/${slug}/${autoLocaleTarget}${location.search}`, { replace: true });
  }, [autoLocaleTarget, slug, navigate, location.search]);

  // One view per page load: once the page has rendered and any language
  // redirect has happened, never in the editor preview. Switching language
  // afterwards is the same visit, so the refs are keyed by slug only.
  const viewSentFor = useRef(null);
  const gtmViewSentFor = useRef(null);
  useEffect(() => {
    if (preview || !page || autoLocaleTarget) return;
    if (viewSentFor.current !== slug) {
      viewSentFor.current = slug;
      // The URL's language is the one served (the page data for it may
      // still be loading right after the redirect).
      sendViewBeacon({ kind: 'page', slug, locale: locale || activeLocale });
    }
    // GTM loads its config asynchronously; trackPageView reports whether it
    // pushed, so the GTM view fires once, as soon as GTM is ready.
    if (gtmViewSentFor.current !== slug && page.title) {
      if (trackPageView(page.title, `/p/${slug}${locale ? `/${locale}` : ''}`)) {
        gtmViewSentFor.current = slug;
      }
    }
  }, [page, slug, locale, activeLocale, autoLocaleTarget, trackPageView, preview]);

  const fetchPage = async () => {
    try {
      // ?preview=true also returns drafts (see the "Full Preview" fix).
      // ?locale=xx merges that language's text in, falling back per field.
      const params = new URLSearchParams();
      if (preview) params.set('preview', 'true');
      if (locale) params.set('locale', locale);
      const qs = params.toString();
      const response = await fetch(
        `${BACKEND_URL}/api/v1/pages/lookup/slug/${slug}${qs ? `?${qs}` : ''}`
      );
      const data = await response.json();

      // For preview, show the page regardless of published status
      if (data.success && (preview || data.data?.status === 'published')) {
        setPage(data.data);
        setLocales(data.meta?.locales || []);
        setActiveLocale(data.meta?.active_locale || data.meta?.default_locale || 'en');
      } else {
        setError('Page not found');
      }
    } catch (err) {
      setError('Failed to load page');
    } finally {
      setLoading(false);
    }
  };

  const fetchBrand = async () => {
    try {
      // Public endpoint: /api/v1/brand/settings requires auth, so anonymous
      // visitors got a 401, brand stayed null, and the footer didn't render
      // at all — it only showed up for logged-in staff previewing.
      const response = await fetch(`${BACKEND_URL}/api/v1/public/brand`);
      const data = await response.json();
      if (data.success) {
        setBrand(data.data);
      }
    } catch (err) {
      console.error('Failed to load brand settings');
    }
  };
  
  const renderSection = (section, index) => {
    if (section.visible === false) return null;
    
    const { type, content } = section;
    
    // Hero and logo_overlay don't get wrapped backgrounds
    if (type === 'hero') {
      return <HeroSection key={section.id} content={content} />;
    }
    if (type === 'logo_overlay') {
      return <LogoOverlaySection key={section.id} content={content} brand={brand} />;
    }
    
    // Determine background class based on section index (alternating)
    const bgClass = index % 2 === 0 ? 'section-bg' : 'section-bg-alt';
    
    let sectionContent;
    switch (type) {
      case 'text':
        sectionContent = <TextSection key={section.id} content={content} />;
        break;
      case 'property_details':
        sectionContent = <PropertyDetailsSection key={section.id} content={content} />;
        break;
      case 'image_gallery':
        sectionContent = <ImageGallerySection key={section.id} content={content} />;
        break;
      case 'features':
        sectionContent = <FeaturesSection key={section.id} content={content} />;
        break;
      case 'agent_card':
        sectionContent = <AgentCardSection key={section.id} content={content} sectionId={section.id} onLead={fireLead} />;
        break;
      case 'map':
        sectionContent = <MapSection key={section.id} content={content} />;
        break;
      case 'video':
        sectionContent = <VideoSection key={section.id} content={content} />;
        break;
      case 'contact_form':
        sectionContent = (
          <ContactFormSection
            key={section.id}
            content={content}
            form={page?.form}
            slug={slug}
            locale={activeLocale}
            onFormSubmit={({ submissionId }) => {
              // Preserve the existing platform GTM event.
              trackFormSubmit(page?.id, page?.title, page?.title);
              // Per-lead-source conversion dispatch (consent-gated, defensive,
              // skipped in preview). submissionId rides through as the Google
              // Ads transaction_id so a retried submit isn't double-counted.
              fireLead('form', { transactionId: submissionId });
            }}
          />
        );
        break;
      case 'pdf_download':
        sectionContent = <PDFDownloadSection key={section.id} content={content} />;
        break;
      case 'document_list':
        sectionContent = <DocumentListSection key={section.id} content={content} />;
        break;
      case 'dynamic_agent':
        sectionContent = <DynamicAgentSection key={section.id} content={content} sectionId={section.id} onLead={fireLead} />;
        break;
      default:
        return null;
    }
    
    // Wrap the section content with the background
    return (
      <div key={section.id} className={bgClass}>
        {sectionContent}
      </div>
    );
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (error || !page) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Page Not Found</h1>
          <p className="text-muted-foreground">The page you're looking for doesn't exist or has been unpublished.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen" data-testid="public-landing-page" style={{
      ...brandStyles,
      backgroundColor: brand?.colors?.background || '#ffffff',
      color: brand?.colors?.text || '#1f2937'
    }}>
      {/* Apply brand color styles */}
      <style>{`
        [data-testid="public-landing-page"] button[class*="bg-primary"],
        [data-testid="public-landing-page"] .bg-primary {
          background-color: var(--brand-button, #144733) !important;
          color: var(--brand-button-text, #ffffff) !important;
        }
        [data-testid="public-landing-page"] button[class*="bg-primary"]:hover,
        [data-testid="public-landing-page"] .bg-primary:hover {
          filter: brightness(0.9);
        }
        [data-testid="public-landing-page"] .text-primary {
          color: var(--brand-primary, #144733) !important;
        }
        [data-testid="public-landing-page"] .bg-primary\\/10 {
          background-color: color-mix(in srgb, var(--brand-primary, #144733) 10%, transparent) !important;
        }
        [data-testid="public-landing-page"] .text-muted-foreground {
          color: var(--brand-text-muted, #6b7280) !important;
        }
        [data-testid="public-landing-page"] .section-bg {
          background-color: var(--brand-section-bg, #ffffff);
        }
        [data-testid="public-landing-page"] .section-bg-alt {
          background-color: var(--brand-section-bg-alt, #f8fafc);
        }
      `}</style>
      
      {/* Sections */}
      <main className="relative">
        {page.sections
          ?.filter(s => s.visible !== false)
          .sort((a, b) => a.order - b.order)
          .map((section, index) => renderSection(section, index))}
      </main>
      
      {/* Language switcher + hreflang — renders nothing for a single-language
          page, so the monolingual case is unchanged. */}
      <LanguageSwitcher
        slug={slug}
        locales={locales}
        activeLocale={activeLocale}
        defaultLocale={page?.default_locale || 'en'}
        preview={preview}
      />

      {/* Footer — shared with the Brand Kit preview so they can't drift */}
      <BrandFooter brand={brand} onLead={fireLead} />

      {/* Consent-gated marketing tags and the site-wide GTM container. Never
          in preview — it would fire real pixels on the author's own view. */}
      {!preview && <TrackingScripts tags={page?.tracking_tags} gtmEnabled={gtmEnabled} />}
    </div>
  );
}
